/**
 * VIZ_TYPE_CONFIG — display metadata for every chart type in the platform.
 *
 * Keys must match the values in CHART_TYPE (lowercased).
 *
 * When a new chart type is added to the backend:
 *   1. Add its key to CHART_TYPE in appConstants.ts
 *   2. Add a matching entry here
 *   3. Everything else (filter chips, card previews, type badges, layout
 *      reference icons, sample-data previews) updates automatically
 *
 * icon     — Material Icons name (https://fonts.google.com/icons) — must exist
 *            in this project's bundled classic "Material Icons" webfont
 *            (src/assets/fonts/material-icons), not just the newer Material
 *            Symbols set, or it silently falls back to rendering literal text.
 * label    — Short human-readable name shown in chips and badges (keep under ~10 chars)
 * accentIdx — Index into CARD_ACCENTS palette (0–5, cycles automatically)
 * bg/color — Icon badge background/foreground used in the Layout tab's
 *            "Add Visualization" reference list and sample-data previews.
 *            Optional — falls back to a neutral grey badge when omitted.
 */

export interface VizTypeConfig {
  label: string;
  icon: string; // Material Icons name — always available, no extra dependency
  accentIdx: number; // 0–5, maps to CARD_ACCENTS in ListViewLayout
  bg?: string;
  color?: string;
}

const DEFAULT_BADGE = { bg: '#F1EFE8', color: '#5F5E5A' };

export const VIZ_TYPE_CONFIG: Record<string, VizTypeConfig> = {
  bar:           { label: 'Bar',          icon: 'bar_chart',        accentIdx: 0, bg: '#EAF3DE', color: '#3B6D11' },
  horizontalbar: { label: 'H. Bar',       icon: 'bar_chart',        accentIdx: 1, bg: '#EAF3DE', color: '#3B6D11' },
  stackedbar:    { label: 'Stacked Bar',  icon: 'stacked_bar_chart', accentIdx: 1, bg: '#EAF3DE', color: '#3B6D11' },
  histogram:     { label: 'Histogram',    icon: 'equalizer',        accentIdx: 5, bg: '#EAF3DE', color: '#3B6D11' },
  multibar:      { label: 'Multi Bar',    icon: 'view_column',      accentIdx: 1, bg: '#EAF3DE', color: '#3B6D11' },
  line:          { label: 'Line',         icon: 'show_chart',       accentIdx: 2, bg: '#FAEEDA', color: '#854F0B' },
  multiline:     { label: 'Multiline',    icon: 'multiline_chart',  accentIdx: 3, bg: '#FAEEDA', color: '#854F0B' },
  area:          { label: 'Area',         icon: 'area_chart',       accentIdx: 3, bg: '#FAEEDA', color: '#854F0B' },
  linebar:       { label: 'L. Bar',       icon: 'show_chart',       accentIdx: 3, bg: '#FAEEDA', color: '#854F0B' },
  pie:           { label: 'Pie',          icon: 'pie_chart',        accentIdx: 4, bg: '#EEEDFE', color: '#534AB7' },
  donut:         { label: 'Donut',        icon: 'donut_large',      accentIdx: 5, bg: '#EEEDFE', color: '#534AB7' },
  table:         { label: 'Table',        icon: 'table_chart',      accentIdx: 0, bg: '#F1EFE8', color: '#5F5E5A' },
  tablebar:      { label: 'T. Bar',       icon: 'view_list',        accentIdx: 1, bg: '#F1EFE8', color: '#5F5E5A' },
  tabview:       { label: 'Tab View',     icon: 'tab',              accentIdx: 4, bg: '#F1EFE8', color: '#5F5E5A' },
  metric:        { label: 'Metric',       icon: 'trending_up',      accentIdx: 2, bg: '#E6F1FB', color: '#185FA5' },
  scatter:       { label: 'Scatter',      icon: 'scatter_plot',     accentIdx: 3, bg: '#FAECE7', color: '#993C1D' },
  bubble:        { label: 'Bubble',       icon: 'bubble_chart',     accentIdx: 4, bg: '#FAECE7', color: '#993C1D' },
  heatmap:       { label: 'Heatmap',      icon: 'grid_on',          accentIdx: 5, bg: '#FAECE6', color: '#993C1D' },
  indiamap:      { label: 'Map',          icon: 'map',              accentIdx: 5, bg: '#E1F5EE', color: '#0F6E56' },
  map:           { label: 'Map',          icon: 'map',              accentIdx: 5, bg: '#E1F5EE', color: '#0F6E56' },
  treemap:       { label: 'Treemap',      icon: 'account_tree',     accentIdx: 0, bg: '#E1F5EE', color: '#0F6E56' },
  funnel:            { label: 'Funnel',       icon: 'filter_list',    accentIdx: 2, bg: '#FCE8F1', color: '#9C1B5E' },
  conversionfunnel:  { label: 'Conv. Funnel', icon: 'trending_down',  accentIdx: 0, bg: '#E3F6FB', color: '#0B6E85' },
  stagefunnel:       { label: 'Stage Funnel', icon: 'linear_scale',   accentIdx: 4, bg: '#FCE8F1', color: '#9C1B5E' },
};

/**
 * getVizTypeConfig — safe lookup with a sensible default.
 * Returns a default entry for any unknown chart type so new types
 * still render a card even before their entry is added here.
 */
export const getVizTypeConfig = (chartType: string): VizTypeConfig => {
  const key = (chartType || '').toLowerCase().replace(/\s+/g, '');
  return (
    VIZ_TYPE_CONFIG[key] ?? {
      label: chartType || 'Chart', // shows the raw type name as fallback
      icon: 'bar_chart', // generic chart icon
      accentIdx: 0,
      ...DEFAULT_BADGE,
    }
  );
};
